<template>
  <div class="flex flex-col gap-5">
    <div v-if="!eventsStore.isEventLoaded" class="py-10 flex justify-center">
      <BaseSpinner />
    </div>
    <div
      v-else-if="eventsStore.event"
      class="flex flex-col gap-5 text-gray-900"
    >
      <div class="flex flex-col gap-5 bg-white rounded-xl p-4 sm:p-6 md:p-8">
        <div class="relative">
          <BaseRatio
            class="rounded-xl md:hidden"
            :ratio="1 / 2"
            size="cover"
            :src="
              eventsStore.event.logo?.url ||
              '/event-default-background-2400x1260.png'
            "
          />
          <BaseRatio
            class="rounded-xl max-md:hidden"
            :ratio="5 / 13"
            size="cover"
            :src="
              eventsStore.event.background?.url ||
              '/event-default-background-2400x1260.png'
            "
          />
          <div v-if="authStore.auth" class="absolute top-3 right-3">
            <FavoritesButton
              :entity-title="eventTitle"
              :entity-id="eventsStore.event.id"
              :entity-type="EntityType.Event"
              button-class="text-primary-900 bg-primary-100 hover:text-primary-900 hover:bg-primary-200 active:bg-primary-200 h-8 px-4"
            />
          </div>
        </div>
        <div class="flex gap-5 flex-wrap justify-between">
          <div class="flex flex-col gap-2">
            <h1 class="font-bold text-3xl mb-2">
              {{ eventTitle }}
            </h1>
            <div
              class="flex flex-col xs:flex-row flex-wrap gap-x-4 gap-y-2 xs:items-center"
            >
              <div class="flex gap-2">
                <BaseIcon
                  name="outline_calendar"
                  size="xs"
                  title="Date"
                  class="mt-[2px] flex-shrink-0"
                />
                <div class="text-sm">
                  {{
                    getLocalCustomDate(eventsStore.event.custom_date) ||
                    getHumanizedDatePeriod(
                      eventsStore.event.date_from,
                      eventsStore.event.date_to,
                      eventsStore.event.timezone
                    )
                  }}
                </div>
              </div>
              <BaseLink
                v-if="googleCalendarLink"
                class="flex gap-2 items-center"
                :href="googleCalendarLink"
                target="_blank"
              >
                <BaseIcon
                  name="google_calendar"
                  size="none"
                  :title="$t('events.add_to_google_calendar')"
                  class="w-[18px] h-[19px] flex-shrink-0"
                />
                <div class="text-sm">
                  {{ $t('events.add_to_google_calendar') }}
                </div>
              </BaseLink>
            </div>
            <div v-if="hubDates" class="flex gap-2">
              <BaseIcon
                name="outline_chat_alt_2"
                size="xs"
                title="online_platform"
                class="mt-[2px] flex-shrink-0"
              />
              <div class="text-sm">
                {{ $t('events.online_platform_availability') + ' ' }}
                <span class="whitespace-nowrap">{{ hubDates }}</span>
              </div>
            </div>
            <div>
              <component
                :is="eventPlaceLink ? BaseLink : BaseFragment"
                class="inline-flex"
                :href="eventPlaceLink"
                target="_blank"
              >
                <span class="inline-flex gap-2">
                  <BaseIcon
                    name="outline_map_pin"
                    size="xs"
                    title="place"
                    class="mt-[2px] flex-shrink-0"
                  />
                  <span class="text-sm">
                    {{ eventPlace }}
                  </span>
                </span>
              </component>
            </div>
          </div>
          <div
            class="flex gap-2 flex-wrap items-start flex-grow md:flex-grow-0 w-full md:w-auto"
          >
            <div
              v-if="isShowOnlinePlatformButton"
              class="flex flex-col gap-1 flex-grow md:flex-grow-0"
            >
              <BaseLink
                :href="getHubLink(eventsStore.event.slug)"
                :class="!isHubOpen && 'pointer-events-none'"
                :target="isApp ? '_self' : '_blank'"
              >
                <BaseButton
                  look="solid"
                  :theme="isHubOpen ? 'primary' : 'disabled'"
                  rounded="xl"
                  size="md"
                  class="w-full"
                  :disabled="!isHubOpen"
                >
                  <div>
                    {{ onlinePlatformButtonText }}
                  </div>
                </BaseButton>
              </BaseLink>
              <div
                v-if="ticketsToActivateCount"
                class="flex items-center justify-center gap-2 text-pro hover:text-pro-300 pb-4 md:pb-0"
              >
                <BaseIcon name="outline_exclamation_thin" size="sm" />
                <BaseLocaleLink to="/users/me/orders">
                  <i18n-t keypath="tickets.tickets_left_to_activate" tag="div">
                    <template #count>
                      {{ ticketsToActivateCount }}
                    </template>
                    <template #ticket>
                      <span class="underline">
                        {{
                          $t('tickets.tickets_forms', {
                            count: ticketsToActivateCount,
                          })
                        }}
                      </span>
                    </template>
                  </i18n-t>
                </BaseLocaleLink>
              </div>
            </div>

            <template v-if="isShowBuyTicketButton">
              <BaseButton
                v-if="hashToScrollForRegistration"
                look="solid"
                theme="primary"
                rounded="xl"
                class="flex-grow md:flex-grow-0"
                size="md"
                @click="scrollToHash(hashToScrollForRegistration)"
              >
                <BaseIcon
                  name="outline_ticket"
                  class="flex-shrink-0 mr-3"
                  size="xs"
                />
                {{ $t('events.register') }}
              </BaseButton>
              <BaseLink
                v-else
                class="flex-grow md:flex-grow-0"
                :href="eventsStore.event.config?.login?.registrationUrl"
                target="_blank"
              >
                <BaseButton
                  look="solid"
                  theme="primary"
                  rounded="xl"
                  class="w-full"
                  size="md"
                >
                  <BaseIcon
                    name="outline_ticket"
                    class="flex-shrink-0 mr-3"
                    size="xs"
                  />
                  {{ $t('events.register') }}
                </BaseButton>
              </BaseLink>
            </template>

            <BaseButton
              v-if="!authStore.auth && isHubOpen"
              class="flex-grow md:flex-grow-0"
              look="ghost"
              theme="info"
              rounded="xl"
              size="md"
              @click="authStore.openLoginModal"
            >
              {{ $t('events.login_to_access') }}
            </BaseButton>

            <BaseButton
              look="ghost"
              theme="info"
              rounded="xl"
              class="flex-grow md:flex-grow-0"
              size="md"
              @click="openShareModal"
            >
              <BaseIcon
                name="outline_share"
                class="flex-shrink-0 mr-3"
                size="xs"
              />
              {{ $t('common.share') }}
            </BaseButton>
          </div>
          <ShareModal
            :is-open="isShareModalOpen"
            :share-options="
              getEventShareOptions(eventLink, eventsStore.event.name)
            "
            :link="eventLink"
            @close="closeShareModal"
          />
        </div>
        <!-- <div v-if="eventsStore.isEventActual(eventsStore.event)">
          <BaseButton
            class="w-full md:w-auto"
            :look="
              reactionsCounters[ReactionType.WantToGo]?.is_set_by_user
                ? 'solid'
                : 'border'
            "
            theme="primary"
            rounded="xl"
            :loading="isCountersProcessing"
            size="md"
            @click="onChangeReaction(ReactionType.WantToGo)"
          >
            <BaseIcon
              name="outline_star"
              class="flex-shrink-0 mr-3"
              size="xs"
            />
            {{ $t('events.want_to_go') }}
          </BaseButton>
        </div> -->
      </div>
      <!-- <SubscriptionsBanner
        v-if="isShowSubscriptionBanner"
        :is-pro-active="authStore.isProActive"
      /> -->
      <PageEditor
        :page-link="eventLink"
        :is-admin="authStore.isAdmin"
        :event-id="eventsStore.event.id"
        :meta-title="eventsStore.event.name"
        :meta-description="eventsStore.event?.preview_description"
        :meta-image="
          eventsStore.event?.logo?.url || '/event-default-logo-1200x630.png'
        "
        :meta-keywords="`event, WN, ${eventsStore.event?.name}`"
      />
      <!-- <div ref="commentsElement" class="p-4 sm:p-8 bg-white rounded-xl">
        <CommentsList
          :id="eventsStore.event.id"
          :total="commentsCounter"
          :entity-type="EntityType.Event"
          @loaded="scrollToComments"
          @create="onCommentCreate"
        />
      </div> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useEvents } from '~/stores/events'
import { useAuth } from '~/stores/auth'
import { EntityType } from '~/models/common'
import {
  EventTypeRoutesMap,
  EventType,
  EventsWithoutCalendarButton,
} from '~/models/events'
// import { ReactionType } from '~/models/counters'
import { useTickets } from '~/stores/tickets'
import { useNotifications } from '~/stores/notifications'
import { removeMarkdown } from '~/utils/remove-markdown'

definePageMeta({
  allowBack: true,
})

const { pushNotification } = useNotifications()
const nuxtApp = useNuxtApp()
const BaseLink = resolveComponent('BaseLink')
const BaseFragment = resolveComponent('BaseFragment')

const eventsStore = useEvents()
const {
  getEventPageRoute,
  getEventPageLink,
  getEventShareOptions,
  getLocalCustomDate,
  getHubLink,
  getEventPlace,
  getEventPlaceLink,
  fetchEvent,
  resetEvent,
  isEventFuture,
  isEventGoingNow,
} = eventsStore
const route = useRoute()
const authStore = useAuth()
const { t } = useI18n()
const { isApp } = useDetect()
const ticketsStore = useTickets()
const {
  countOrderItemsToActivate,
  fetchOrdersByEventId,
  // fetchTicketListDetails,
} = ticketsStore
const { getHumanizedDatePeriod, getHumanizedDate, parseDate } = useDate()
const { getMarkedTextWithBlankLinks } = useMarkedText()

const eventSlug = route.params.event as string

const eventLink = computed(() => {
  return getEventPageLink(getEventPageRoute(eventSlug, eventsStore.event?.type))
})

const isShareModalOpen = ref(false)
const isLoaded = ref(false)

const openShareModal = () => {
  isShareModalOpen.value = true
}

const closeShareModal = () => {
  isShareModalOpen.value = false
}

function scrollToHash(hash: string) {
  const element = document.querySelector(hash)
  if (!element) {
    pushNotification({
      title: 'No block with such hash on the page',
      theme: 'destructive',
    })
    return
  }
  element.scrollIntoView({ behavior: 'smooth' })
}

const hashToScrollForRegistration = computed(() => {
  const registrationUrl = eventsStore.event?.config?.login?.registrationUrl
  const currentPath = useRoute().path

  if (!registrationUrl) {
    return null
  }

  try {
    const url = new URL(registrationUrl)

    if (
      url.host === 'wnhub.io' &&
      currentPath.includes(url.pathname) &&
      url.hash
    ) {
      return url.hash
    }
  } catch (e: any) {
    return null
  }
  return null
})

const eventTitle = computed<string>(
  () => eventsStore.event?.tagline || eventsStore.event?.name || ''
)

const hubDates = computed(() => {
  if (
    eventsStore.event &&
    eventsStore.event.hub_date_from &&
    eventsStore.event.hub_date_to
  ) {
    return getHumanizedDatePeriod(
      eventsStore.event.hub_date_from,
      eventsStore.event.hub_date_to,
      eventsStore.event.timezone
    )
  }
  return ''
})

const eventPlace = computed(() => {
  return eventsStore.event ? getEventPlace(eventsStore.event) : ''
})

const eventPlaceLink = computed(() => {
  return eventsStore.event ? getEventPlaceLink(eventsStore.event) : ''
})

const isHubOpen = computed(() =>
  eventsStore.event ? eventsStore.isHubOpen(eventsStore.event) : false
)

const isShowOnlinePlatformButton = computed(
  () =>
    !!authStore.auth &&
    (!!eventsStore.event?.is_has_ticket || !!eventsStore.event?.auto_reg)
)

const isShowBuyTicketButton = computed(
  () =>
    !!eventsStore.event &&
    eventsStore.isEventActual(eventsStore.event) &&
    !!eventsStore.event?.config?.login?.registrationUrl &&
    !eventsStore.event?.is_has_ticket
)

const onlinePlatformButtonText = computed(() => {
  if (!eventsStore.event) {
    return ''
  }

  // if event is in future
  if (isEventFuture(eventsStore.event)) {
    const date = getHumanizedDate(
      eventsStore.event.hub_date_from || eventsStore.event.date_from,
      eventsStore.event.timezone
    )

    return `${t('events.available_from')} ${date}`
  }

  // if event is going right now (not hub, only event)
  if (isEventGoingNow(eventsStore.event)) {
    return t('events.join')
  }

  // if event is finished but hub is still open
  if (isHubOpen.value) {
    const date = getHumanizedDate(
      eventsStore.event.hub_date_to || eventsStore.event.date_to,
      eventsStore.event.timezone
    )

    return `${t('events.available_until')} ${date}`
  }

  // if event and hub are closed
  if (
    !isHubOpen.value &&
    (eventsStore.event.hub_date_to || eventsStore.event.date_to)
  ) {
    const date = getHumanizedDate(
      eventsStore.event.hub_date_to || eventsStore.event.date_to,
      eventsStore.event.timezone
    )

    return `${t('events.finished_on')} ${date}`
  }

  return t('events.finished')
})

const {
  initCountersHelper,
  // commentsCounter,
  // updateCommentsCounter,
  // reactionsCounters,
  // onChangeReaction,
  // isCountersProcessing,
} = useCountersHelper()

// const commentsElement = ref<HTMLElement | null>(null)
// function scrollToComments() {
//   route.query.scroll === 'comments' && commentsElement.value?.scrollIntoView()
// }

// function onCommentCreate() {
//   updateCommentsCounter(commentsCounter.value + 1)
// }

const ticketsToActivateCount = computed(() => {
  return ticketsStore.ordersByEventId.reduce((acc, order) => {
    const orderCount = countOrderItemsToActivate(order)
    acc += orderCount
    return acc
  }, 0)
})

// const isShowSubscriptionBanner = computed(() => {
//   return (
//     eventsStore.event &&
//     eventsStore.isEventAvailableForPro(eventsStore.event) &&
//     ticketsStore.ticketsDetails.some((ticket) =>
//       ticket.features.some((feature) => feature.feature === 'pro_account')
//     )
//   )
// })

const GoogleCalendarLinkLabel: Record<EventType, string> = {
  [EventType.Default]: t('events.event_page'),
  [EventType.Course]: t('events.course_page'),
}

const googleCalendarLink = computed<string>(() => {
  if (!eventsStore.event || EventsWithoutCalendarButton.includes(eventSlug)) {
    return ''
  }

  const url = new URL('https://www.google.com/calendar/render')
  url.searchParams.set('action', 'TEMPLATE')
  url.searchParams.set('text', eventTitle.value)
  url.searchParams.set('details', googleCalendarDescription.value)
  url.searchParams.set(
    'location',
    eventsStore.event.full_address ||
      eventsStore.event.address ||
      eventsStore.event.city ||
      ''
  )

  const startDate = formatGoogleCalendarDate(eventsStore.event.date_from)

  const endDate = formatGoogleCalendarDate(eventsStore.event.date_to)

  url.searchParams.set(
    'dates',
    startDate && endDate && `${startDate}/${endDate}`
  )

  return url.toString()
})

const googleCalendarDescription = computed(() => {
  if (!eventsStore.event) {
    return ''
  }

  const description =
    getMarkedTextWithBlankLinks(eventsStore.event?.description || '') ||
    eventsStore.event?.preview_description ||
    ''

  const link = eventLink.value
  const linkLabel = GoogleCalendarLinkLabel[eventsStore.event.type]
  const linkText = `${linkLabel}: <a href="${link}">${link}</a>`

  return [description, linkText].filter((text) => text).join('\n\n')
})

function formatGoogleCalendarDate(date: string): string {
  const parsedDate = parseDate(date, 'UTC')

  return parsedDate && parsedDate.format('YYYYMMDDTHHmmss[Z]')
}

const initGoogleEventsScript = () => {
  if (
    !eventsStore.event ||
    eventsStore.event.type !== EventType.Default ||
    !eventsStore.event.is_show
  ) {
    return
  }

  const getEventAttendanceMode = () => {
    if (eventsStore.event?.is_online && eventsStore.event?.is_onsite) {
      return 'https://schema.org/MixedEventAttendanceMode'
    } else if (eventsStore.event?.is_onsite) {
      return 'https://schema.org/OfflineEventAttendanceMode'
    } else {
      return 'https://schema.org/OnlineEventAttendanceMode'
    }
  }

  const offlineLocation = {
    '@type': 'Place',
    address:
      eventsStore.event.full_address ||
      eventsStore.event.address ||
      eventsStore.event.city ||
      '',
    url: eventsStore.event.address_link || '',
  }

  const onlineLocation = {
    '@type': 'VirtualLocation',
    url: eventLink.value,
  }

  const getLocation = () => {
    if (eventsStore.event?.is_online && eventsStore.event?.is_onsite) {
      return [offlineLocation, onlineLocation]
    } else if (eventsStore.event?.is_onsite) {
      return offlineLocation
    } else {
      return onlineLocation
    }
  }

  const formatDate = (date: string, timezone: string) => {
    const parsedDate = parseDate(date, timezone)

    return parsedDate ? parsedDate.format('YYYY-MM-DDTHH:mmZ') : ''
  }

  const validFromParsedDate = parseDate(
    eventsStore.event.date_from,
    eventsStore.event.timezone
  )

  const validFrom = validFromParsedDate
    ? validFromParsedDate.subtract(1, 'month').format('YYYY-MM-DDTHH:mm')
    : ''

  const offers = ticketsStore.tickets.map((ticket) => ({
    '@type': 'Offer',
    url: eventsStore.event?.config?.login?.registrationUrl || eventLink.value,
    price: (ticket.price[ticket.main_currency] || 0) / 100,
    priceCurrency: ticket.main_currency,
    category: ticket.name,
    availability: 'https://schema.org/InStock',
    validFrom,
  }))

  const fullDescription = eventsStore.event?.description
    ? removeMarkdown(eventsStore.event.description, {
        listUnicodeChar: '-',
      }).replaceAll('\n', ' ')
    : ''

  const googleEventsScript = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: eventTitle.value,
    startDate: formatDate(
      eventsStore.event.date_from,
      eventsStore.event.timezone
    ),
    endDate: formatDate(eventsStore.event.date_to, eventsStore.event.timezone),
    eventAttendanceMode: getEventAttendanceMode(),
    eventStatus: 'https://schema.org/EventScheduled',
    location: getLocation(),
    image: [eventsStore.event.logo?.url || '/event-default-logo-1200x630.png'],
    description:
      fullDescription ||
      eventsStore.event.preview_description ||
      eventTitle.value,
    offers,
    performer: {
      '@type': 'PerformingGroup',
      name: 'WN Media Group',
    },
    organizer: {
      '@type': 'Organization',
      name: 'WN Media Group',
      url: 'https://wnhub.io/',
    },
  }

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(googleEventsScript),
      },
    ],
  })
}

const initMeta = () => {
  useHead({
    link: [
      {
        rel: 'canonical',
        href: eventLink.value,
      },
    ],
  })
}

const load = async () => {
  isLoaded.value = false
  try {
    await fetchEvent(eventSlug, !!authStore.auth)
  } catch (error: any) {
    throw useCreateScreenError({ message: 'no such event' })
  }
  await ticketsStore.fetchTicketList(eventsStore.event?.id)
  isLoaded.value = true

  if (!eventsStore.event) {
    return
  }

  if (
    process.client &&
    !route.fullPath.includes('/' + EventTypeRoutesMap[eventsStore.event.type])
  ) {
    await navigateTo(
      useLocaleRoute()(
        `/${EventTypeRoutesMap[eventsStore.event.type]}/${eventSlug}`
      )
    )
    return
  }

  nuxtApp.runWithContext(() => {
    initMeta()
    initGoogleEventsScript()
  })

  try {
    // TODO: uncomment for PRO banner
    // await fetchTicketListDetails(eventsStore.event.id)

    if (authStore.auth) {
      await fetchOrdersByEventId(eventsStore.event.id)
    }
  } catch (error: any) {
    if (error.statusCode !== 404) {
      throw error
    }
  }

  if (process.client) {
    const entity = {
      entity_id: eventsStore.event.id,
      entity_type: EntityType.Event,
    }

    await initCountersHelper({
      entity,
      isLoadComments: true,
      isLoadReactions: true,
    })
  }
}

onMounted(async () => {
  await load()
})

onServerPrefetch(async () => {
  await load()
})

watch(
  () => !!authStore.auth,
  async (isAuth) => {
    resetEvent()
    try {
      await fetchEvent(eventSlug, isAuth)
    } catch (error: any) {
      throw useCreateScreenError({ message: 'no such event' })
    }

    if (isAuth && eventsStore.event) {
      await fetchOrdersByEventId(eventsStore.event.id)
    }
  }
)
</script>

<style scoped lang="scss">
.list-group {
  min-height: 50px;
}
</style>
